import {useMutation} from "@apollo/client";

import {ActionDialog} from "@/components/overlay/ActionDialog.tsx";
import {GQL_MAINTENANCE_SCHEDULE_EDIT, GQL_MAINTENANCE_SCHEDULES_LIST} from "@/lib/queries/maintenance.ts";
import {GraphQLResponse} from "@/lib/types/common.ts";
import {MaintenanceSchedule} from "@/lib/types/maintenance.ts";
import {getNextOccurrence} from "@/pages/maintenance/maintenance-dashboard/tabs/schedules/MaintenanceSchedulesTable/MaintenanceSchedulesTable.utils.ts";

type MaintenancePauseConfirmationProps = {
  schedule: MaintenanceSchedule;
  show: boolean;
  setShow: (show: boolean) => void;
};

const ScheduleInMaintenance = (schedule: MaintenanceSchedule) => {
  const nextOccurrence = getNextOccurrence(schedule);
  if (!schedule.is_active || !nextOccurrence) {
    return false;
  }
  if (!nextOccurrence.ends_at) {
    return true;
  }
  const now = new Date();
  const startsAt = new Date(nextOccurrence.starts_at);
  const endsAt = new Date(nextOccurrence.ends_at);
  return now >= startsAt && now <= endsAt;
};

export const useMaintenancePauseResume = (schedule: MaintenanceSchedule) => {
  const [updateMaintenanceSchedule] = useMutation<GraphQLResponse<null>>(GQL_MAINTENANCE_SCHEDULE_EDIT, {
    refetchQueries: [GQL_MAINTENANCE_SCHEDULES_LIST],
  });

  const resumeSchedule = async () => {
    await updateMaintenanceSchedule({
      variables: {
        id: schedule.id,
        data: {
          is_active: true,
        },
      },
    });
  };

  const pauseSchedule = async () => {
    await updateMaintenanceSchedule({
      variables: {
        id: schedule.id,
        data: {
          is_active: false,
        },
      },
    });
  };

  return {resumeSchedule, pauseSchedule};
};

export const MaintenancePauseConfirmation = ({schedule, show, setShow}: MaintenancePauseConfirmationProps) => {
  const isInMaintenance = ScheduleInMaintenance(schedule);
  const {pauseSchedule} = useMaintenancePauseResume(schedule);

  return (
    <ActionDialog
      show={show}
      title="Pause Schedule"
      confirmBtnText="Confirm"
      onCancel={() => setShow(false)}
      onConfirm={() => {
        setShow(false);
        pauseSchedule();
      }}
    >
      <div>
        Are you sure you want to pause the Maintenance Schedule{" "}
        <span className={"pulseTextBodyBold"}>{schedule.name}</span>? This schedule will no longer be active for any of
        the Checks linked.
        {isInMaintenance && (
          <div className={"mt-2"}>
            This schedule is currently in maintenance, pausing it now will not affect the ongoing maintenance
            occurrence, but will prevent future occurrences from being scheduled until it is resumed.
          </div>
        )}
      </div>
    </ActionDialog>
  );
};
